/**
 * JIRA Importer
 * Executes the import of JIRA items into Peepz work items
 */

/**
 * Find or create a person by email/name
 */
async function findOrCreatePerson(supabase, assigneeName, projectId) {
  if (!assigneeName) return null;
  
  // Try to find by email or name
  const { data: people } = await supabase
    .from('people')
    .select('id')
    .or(`email.ilike.%${assigneeName}%,fullname.ilike.%${assigneeName}%`)
    .limit(1);
  
  if (people && people.length > 0) {
    return people[0].id;
  }
  
  // Could create a new person here, but for now return null
  return null;
}

/**
 * Execute the import
 */
export async function executeImport(
  supabase,
  user,
  parsedData,
  epicMappings,
  taskMappings, // Map of task issueKey -> epic index (e.g., "epic_0") or subepicId
  strategy,
  replaceAllWork = false
) {
  if (!supabase || !user || !parsedData || !epicMappings || !taskMappings) {
    return {
      success: false,
      error: 'Missing required parameters for import',
      createdItems: { epics: [], subepics: [], tasks: [] },
    };
  }
  
  const createdItems = {
    epics: [],
    subepics: [],
    tasks: [],
  };
  
  const projectId = user.proj?.id;
  const userId = user.id;
  
  if (!projectId || !userId) {
    return {
      success: false,
      error: 'Invalid user or project data',
      createdItems,
    };
  }
  
  // Ensure taskMappings is a Map
  const taskMappingsMap = taskMappings instanceof Map ? taskMappings : new Map(Object.entries(taskMappings || {}));
  
  // Map to convert epic indices to subepic IDs after creation
  const epicIndexToSubepicId = new Map();
  
  try {
    // If replaceAllWork is true, delete all existing work items first (except stories)
    if (replaceAllWork) {
      // First, get all goals for this project to clean up their workitems_order
      const { data: goals } = await supabase
        .from('goals')
        .select('id, workitems_order')
        .eq('project_id', projectId);
      
      // Mark all stories as COMPLETE instead of deleting them
      const { error: storiesError } = await supabase
        .from('work')
        .update({ status: 'COMPLETE' })
        .eq('project_id', projectId)
        .eq('type', 'story');
      
      if (storiesError) {
        console.error('Error marking stories as complete:', storiesError);
        throw new Error(`Failed to mark stories as complete: ${storiesError.message}`);
      }
      
      // Delete all non-story work items (epics, subepics, tasks)
      const { error: deleteError } = await supabase
        .from('work')
        .delete()
        .eq('project_id', projectId)
        .neq('type', 'story');
      
      if (deleteError) {
        console.error('Error deleting existing work:', deleteError);
        throw new Error(`Failed to delete existing work: ${deleteError.message}`);
      }
      
      // Clean up goals' workitems_order arrays (set to null or empty)
      if (goals && goals.length > 0) {
        for (const goal of goals) {
          await supabase
            .from('goals')
            .update({ workitems_order: null })
            .eq('id', goal.id);
        }
      }
    }
    // Step 1: Create Epics (if strategy is epics_as_epics)
    if (strategy === 'epics_as_epics') {
      for (let i = 0; i < epicMappings.length; i++) {
        const mapping = epicMappings[i];
        const { jiraEpic } = mapping;
        
        const { data: newEpic, error: epicError } = await supabase
          .from('work')
          .insert({
            title: jiraEpic.summary || 'Untitled Epic',
            description: jiraEpic.description || null,
            type: 'epic',
            parent_id: null,
            project_id: projectId,
            created_by: userId,
            owner_id: userId,
            child_order: [],
          })
          .select()
          .single();
        
        if (epicError) {
          console.error('Error creating epic:', epicError);
          throw epicError;
        }
        
        createdItems.epics.push({
          jiraEpic,
          peepzEpic: newEpic,
        });
        
        // Create a default subepic under this epic for tasks
        const { data: defaultSubepic, error: subepicError } = await supabase
          .from('work')
          .insert({
            title: `${jiraEpic.summary} - Work`,
            description: null,
            type: 'subepic',
            parent_id: newEpic.id,
            project_id: projectId,
            created_by: userId,
            owner_id: userId,
            child_order: [],
          })
          .select()
          .single();
        
        if (!subepicError && defaultSubepic) {
          createdItems.subepics.push({
            jiraEpic,
            peepzSubepic: defaultSubepic,
          });
          
          // Map epic index to subepic ID
          epicIndexToSubepicId.set(`epic_${i}`, defaultSubepic.id);
          
        // Also map tasks under this JIRA Epic to this subepic (if not already mapped)
        if (parsedData.parentChildMap) {
          const epicChildren = parsedData.parentChildMap.get(jiraEpic.issueKey) || [];
          epicChildren.forEach(child => {
            if (!taskMappingsMap.has(child.issueKey)) {
              taskMappingsMap.set(child.issueKey, defaultSubepic.id);
            }
          });
        }
        }
      }
    }
    
    // Step 2: Create Subepics (if strategy is all_to_subepics)
    if (strategy === 'all_to_subepics') {
      // First, collect all unique target epics and create them
      const targetEpicMap = new Map(); // Map of epic title -> epic ID
      
      // Get all unique target epics from mappings
      const uniqueTargetEpics = new Map();
      epicMappings.forEach((mapping) => {
        const { targetEpic } = mapping;
        if (targetEpic && !uniqueTargetEpics.has(targetEpic.title)) {
          uniqueTargetEpics.set(targetEpic.title, targetEpic);
        }
      });
      
      // Create all target epics first (if they don't already exist)
      for (const [title, targetEpic] of uniqueTargetEpics) {
        let targetEpicId = targetEpic.id;
        
        // If epic doesn't have an ID (it's a default epic or was deleted), check if it exists or create it
        if (!targetEpicId) {
          // Check if epic with this title already exists (in case it wasn't deleted)
          const { data: existingEpic } = await supabase
            .from('work')
            .select('id')
            .eq('project_id', projectId)
            .eq('type', 'epic')
            .eq('title', title)
            .is('parent_id', null)
            .maybeSingle();
          
          if (existingEpic) {
            targetEpicId = existingEpic.id;
          } else {
            // Create new epic
            const { data: newEpic, error: epicError } = await supabase
              .from('work')
              .insert({
                title: targetEpic.title,
                description: targetEpic.description || null,
                type: 'epic',
                parent_id: null,
                project_id: projectId,
                created_by: userId,
                owner_id: userId,
                child_order: [],
              })
              .select()
              .single();
            
            if (epicError) {
              console.error('Error creating target epic:', epicError);
              throw epicError;
            }
            
            targetEpicId = newEpic.id;
            createdItems.epics.push({
              jiraEpic: null,
              peepzEpic: newEpic,
            });
          }
        } else {
          // Epic has an ID, verify it still exists (in case replaceAllWork deleted it)
          const { data: existingEpic } = await supabase
            .from('work')
            .select('id')
            .eq('id', targetEpicId)
            .eq('project_id', projectId)
            .maybeSingle();
          
          if (!existingEpic) {
            // Epic was deleted, create it again
            const { data: newEpic, error: epicError } = await supabase
              .from('work')
              .insert({
                title: targetEpic.title,
                description: targetEpic.description || null,
                type: 'epic',
                parent_id: null,
                project_id: projectId,
                created_by: userId,
                owner_id: userId,
                child_order: [],
              })
              .select()
              .single();
            
            if (epicError) {
              console.error('Error recreating target epic:', epicError);
              throw epicError;
            }
            
            targetEpicId = newEpic.id;
            createdItems.epics.push({
              jiraEpic: null,
              peepzEpic: newEpic,
            });
          }
        }
        
        // Store the epic ID for this title
        targetEpicMap.set(title, targetEpicId);
      }
      
      // Now create subepics under their target epics
      for (let i = 0; i < epicMappings.length; i++) {
        const mapping = epicMappings[i];
        const { jiraEpic, targetEpic } = mapping;
        
        // Get the target epic ID from our map
        const targetEpicId = targetEpicMap.get(targetEpic.title);
        
        if (!targetEpicId) {
          console.error(`No target epic ID found for: ${targetEpic.title}`);
          continue;
        }
        
        // Create subepic under target epic
        const { data: newSubepic, error: subepicError } = await supabase
          .from('work')
          .insert({
            title: jiraEpic.summary || 'Untitled Subepic',
            description: jiraEpic.description || null,
            type: 'subepic',
            parent_id: targetEpicId,
            project_id: projectId,
            created_by: userId,
            owner_id: userId,
            child_order: [],
          })
          .select()
          .single();
        
        if (subepicError) {
          console.error('Error creating subepic:', subepicError);
          throw subepicError;
        }
        
        createdItems.subepics.push({
          jiraEpic,
          peepzSubepic: newSubepic,
        });
        
        // Map epic index to subepic ID
        epicIndexToSubepicId.set(`epic_${i}`, newSubepic.id);
        
        // Also map tasks under this JIRA Epic to this subepic (if not already mapped)
        if (parsedData.parentChildMap) {
          const epicChildren = parsedData.parentChildMap.get(jiraEpic.issueKey) || [];
          epicChildren.forEach(child => {
            if (!taskMappingsMap.has(child.issueKey)) {
              taskMappingsMap.set(child.issueKey, newSubepic.id);
            }
          });
        }
      }
    }
    
    // Convert epic index mappings to actual subepic IDs
    const finalTaskMappings = new Map();
    taskMappingsMap.forEach((value, key) => {
      if (typeof value === 'string' && value.startsWith('epic_')) {
        // Convert epic index to subepic ID
        const subepicId = epicIndexToSubepicId.get(value);
        if (subepicId) {
          finalTaskMappings.set(key, subepicId);
        } else {
          console.warn(`No subepic ID found for epic index ${value}`);
        }
      } else {
        // Already a subepic ID
        finalTaskMappings.set(key, value);
      }
    });
    
    // Step 3: Create Tasks (from both JIRA Stories and Tasks)
    const allTasks = [...(parsedData.stories || []), ...(parsedData.tasks || [])];
    
    for (const jiraTask of allTasks) {
      const subepicId = finalTaskMappings.get(jiraTask.issueKey);
      
      if (!subepicId) {
        console.warn(`No subepic mapping found for task ${jiraTask.issueKey}, skipping`);
        continue;
      }
      
      // Find assignee
      const ownerId = jiraTask.assignee 
        ? await findOrCreatePerson(supabase, jiraTask.assignee, projectId) || userId
        : userId;
      
      const { data: newTask, error: taskError } = await supabase
        .from('work')
        .insert({
          title: jiraTask.summary || 'Untitled Task',
          description: jiraTask.description || null,
          type: 'task',
          parent_id: subepicId,
          story_id: null, // Tasks imported from JIRA don't have stories
          project_id: projectId,
          created_by: userId,
          owner_id: ownerId,
          status: jiraTask.status || 'TODO',
          estimate: jiraTask.storyPoints || null,
          child_order: [],
        })
        .select()
        .single();
      
      if (taskError) {
        console.error('Error creating task:', taskError);
        throw taskError;
      }
      
      createdItems.tasks.push({
        jiraTask,
        peepzTask: newTask,
      });
    }
    
    // Step 4: Update subepic child_order arrays with all tasks that belong to them
    // Group tasks by subepic ID
    const tasksBySubepic = new Map();
    createdItems.tasks.forEach(({ peepzTask }) => {
      if (peepzTask && peepzTask.parent_id) {
        if (!tasksBySubepic.has(peepzTask.parent_id)) {
          tasksBySubepic.set(peepzTask.parent_id, []);
        }
        tasksBySubepic.get(peepzTask.parent_id).push(peepzTask.id);
      }
    });
    
    // Update each subepic's child_order
    for (const [subepicId, taskIds] of tasksBySubepic) {
      const { error: updateError } = await supabase
        .from('work')
        .update({ child_order: taskIds })
        .eq('id', subepicId)
        .eq('type', 'subepic');
      
      if (updateError) {
        console.error(`Error updating child_order for subepic ${subepicId}:`, updateError);
        // Don't throw - this is not critical enough to fail the entire import
      }
    }
    
    return {
      success: true,
      createdItems,
      summary: {
        epics: createdItems.epics.length,
        subepics: createdItems.subepics.length,
        tasks: createdItems.tasks.length,
      }
    };
  } catch (error) {
    console.error('Import error:', error);
    return {
      success: false,
      error: error.message,
      createdItems,
    };
  }
}

